import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '../../i18n/I18nContext';
import './PasswordInput.css';

interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  inputClassName?: string;
  toggleButtonClassName?: string;
  usernameValue?: string;
  usernameName?: string;
  inputStyle?: React.CSSProperties;
  toggleButtonStyle?: React.CSSProperties;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  className = '',
  inputClassName = '',
  toggleButtonClassName = '',
  usernameValue,
  usernameName = 'username',
  inputStyle,
  toggleButtonStyle,
  autoComplete = 'current-password',
  ...inputProps
}) => {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  function showSecret() {
    setVisible(true);
  }

  function hideSecret() {
    setVisible(false);
  }

  return (
    <div className={`password-input ${className}`.trim()}>
      {typeof usernameValue === 'string' ? (
        <input
          type="text"
          className="password-input-hidden-username"
          value={usernameValue}
          name={usernameName}
          autoComplete="username"
          readOnly
          tabIndex={-1}
          aria-hidden="true"
        />
      ) : null}
      <input
        {...inputProps}
        type={visible ? 'text' : 'password'}
        className={`password-input-field ${inputClassName}`.trim()}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        style={inputStyle}
      />
      <button
        type="button"
        className={`password-input-toggle ${toggleButtonClassName}`.trim()}
        onPointerDown={(event) => {
          event.preventDefault();
          showSecret();
        }}
        onPointerUp={hideSecret}
        onPointerLeave={hideSecret}
        onPointerCancel={hideSecret}
        onBlur={hideSecret}
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            showSecret();
          }
        }}
        onKeyUp={(event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            hideSecret();
          }
        }}
        aria-label={visible ? t('passwordInput.hide') : t('passwordInput.show')}
        title={visible ? t('passwordInput.hide') : t('passwordInput.show')}
        style={toggleButtonStyle}
      >
        <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
      </button>
    </div>
  );
};

export default PasswordInput;
